import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { EndUser } from "../../components/EndUser";
import { ErrorState, Skeleton } from "../../components/States";
import { PageHeader } from "../../components/ui";
import { formatDateTime } from "../../lib/format";
import { LineChart } from "../insights/charts";
import { useSlowPending, WakingNumbers } from "../insights/WakingNumbers";
import { SeverityBadge, kindLabel } from "../insights/components";
import { useAuth } from "../../auth/store";
import { allowsFeature, useOrgEntitlements } from "../billing/entitlements";
import { callKeys, callsApi, type CallSeriesPoint } from "./api";
import { CallStateBadge, MediaErrorBadges, PathBadge, QualityMeter, dur, pctText } from "./components";
import { CallUpgrade } from "./plan";
import "./calls.css";

const SEV_CLASS: Record<string, string> = { error: "danger", warn: "warn", info: "neutral" };

/** One small multiple per metric: never two y scales on one chart. */
const METRICS: Array<{ key: keyof CallSeriesPoint; label: string; unit: string; color: string; yMax?: number; format: (v: number) => string }> = [
  { key: "rttMs", label: "Round trip time", unit: "ms", color: "var(--viz-s1)", format: (v) => `${Math.round(v)} ms` },
  { key: "lossPct", label: "Packet loss", unit: "%", color: "var(--viz-critical)", yMax: 100, format: (v) => `${v.toFixed(1)}%` },
  { key: "jitterMs", label: "Jitter", unit: "ms", color: "var(--viz-s4)", format: (v) => `${Math.round(v)} ms` },
  { key: "fps", label: "Frame rate", unit: "fps", color: "var(--viz-s3)", format: (v) => `${Math.round(v)} fps` },
  { key: "kbps", label: "Bitrate", unit: "kbps", color: "var(--viz-s7)", format: (v) => `${Math.round(v)} kbps` },
  { key: "concealmentPct", label: "Audio concealment", unit: "%", color: "var(--viz-s5)", yMax: 100, format: (v) => `${v.toFixed(1)}%` },
  { key: "freezeMs", label: "Freezes", unit: "ms", color: "var(--viz-serious)", format: (v) => `${Math.round(v)} ms` },
  { key: "audioLevel", label: "Audio level", unit: "0–1", color: "var(--viz-s2)", yMax: 1, format: (v) => v.toFixed(2) },
];

/** Freeze time and audio level are omitted from a sample when they are zero, so a chart that has any of them treats the gaps as zero rather than as holes. */
const FILL_GAPS = new Set<keyof CallSeriesPoint>(["freezeMs", "audioLevel"]);

/** A titled group of label/value rows in the summary card. */
function Group({ title, rows }: { title: string; rows: Array<[string, ReactNode]> }) {
  return (
    <section className="call-group" aria-label={title}>
      <h3>{title}</h3>
      <dl className="call-kv">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function CallDetailPage() {
  const auth = useAuth();
  const ent = useOrgEntitlements(auth.currentOrgId);
  const { callId } = useParams({ strict: false }) as { callId: string };
  const navigate = useNavigate();
  // The list's filters travel with the call, so Back lands on the same list.
  const listSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const q = useQuery({ queryKey: callKeys.detail(callId), queryFn: () => callsApi.get(callId) });
  const slowLoad = useSlowPending(q.isLoading);
  const c = q.data;

  const startMs = useMemo(() => (c ? Date.parse(c.startedAt) : 0), [c]);

  /**
   * Open the replay at the moment of a marker. Markers are wall-clock, and the
   * call may start well after the session did, so the replay resolves the
   * offset itself rather than being handed a call-relative one.
   */
  const seekTo = (absMs: number) => {
    if (!c) return;
    void navigate({ to: "/sessions/$id", params: { id: c.sessionId }, search: { at: Math.round(absMs) } as never });
  };

  const backSearch = { ...listSearch, tab: "calls" };
  const back = (
    <Link to="/calls" search={backSearch as never} className="btn ghost call-back">
      ← Calls
    </Link>
  );

  if (ent.isSuccess && !allowsFeature(ent.data, "webrtc")) return <CallUpgrade kind="diagnostics" />;

  if (q.isLoading) {
    return (
      <div className="call-detail">
        <PageHeader back={back} title="Call" />
        {slowLoad ? <WakingNumbers /> : <Skeleton lines={6} />}
      </div>
    );
  }
  if (q.isError || !c) {
    return (
      <div className="call-detail">
        <PageHeader back={back} title="Call" />
        <ErrorState error={q.error ?? new Error("Call not found")} retry={() => void q.refetch()} />
      </div>
    );
  }

  const series = c.series ?? [];
  const present = METRICS.filter((m) => series.some((p) => typeof p[m.key] === "number"));
  // A score with no measured inputs is the zero-input default (100). That is not
  // a measurement, so the meter stays empty until a rollup is actually present.
  const measured = [c.rttP50Ms, c.rttP95Ms, c.lossPctAvg, c.lossPctMax, c.jitterMsAvg, c.fpsAvg, c.concealmentPctAvg].some((n) => n != null) || c.freezeCount > 0;
  const problems = (c.mediaErrors?.length ?? 0) > 0 || c.oneWayAudio || c.oneWayVideo;
  const limited = [c.cpuLimitedPct ? `CPU ${pctText(c.cpuLimitedPct, 0)}` : "", c.bandwidthLimitedPct ? `bandwidth ${pctText(c.bandwidthLimitedPct, 0)}` : ""].filter(Boolean).join(" · ") || "no";
  const resolution = c.resolutionMin && c.resolutionMax ? (c.resolutionMin === c.resolutionMax ? c.resolutionMax : `${c.resolutionMax} → ${c.resolutionMin}`) : "—";

  return (
    <div className="call-detail">
      <PageHeader
        back={back}
        title={`Call ${c.pc}`}
        subtitle={`${formatDateTime(c.startedAt)} · ${dur(c.durationMs)}`}
        actions={
          <Link className="btn primary" to="/sessions/$id" params={{ id: c.sessionId }} search={{ at: startMs } as never}>
            Open replay
          </Link>
        }
      />

      <section className="card calls-card call-head" aria-label="Call summary">
        <div className="call-head-q">
          <QualityMeter score={measured ? c.qualityScore : undefined} width={160} />
          <div className="call-head-badges">
            <CallStateBadge call={c} />
            <PathBadge relayed={c.relayed} />
            {problems && (
              <>
                <MediaErrorBadges errors={c.mediaErrors ?? []} />
                {c.oneWayAudio && <span className="badge danger" title="Audio flowed in one direction only">one-way audio</span>}
                {c.oneWayVideo && <span className="badge danger" title="Video flowed in one direction only">one-way video</span>}
              </>
            )}
          </div>
        </div>
        <div className="call-groups">
          <Group
            title="Connection"
            rows={[
              ["Network", c.networkType ?? "—"],
              ["Setup", dur(c.setupMs)],
              ["ICE restarts", c.iceRestarts || "none"],
              ["Path changes", c.candidatePairChanges || "none"],
              ...(c.callGroupId ? [["Group", <Link key={c.callGroupId} to="/calls/groups/$groupId" params={{ groupId: c.callGroupId }}>{c.callGroupId}</Link>] as [string, ReactNode]] : []),
            ]}
          />
          <Group
            title="Quality"
            rows={[
              ["Round trip p50 / p95", `${dur(c.rttP50Ms)} / ${dur(c.rttP95Ms)}`],
              ["Loss avg / max", `${pctText(c.lossPctAvg)} / ${pctText(c.lossPctMax)}`],
              ["Jitter avg", dur(c.jitterMsAvg)],
              ["Freezes", c.freezeCount ? `${c.freezeCount} · ${dur(c.freezeMsTotal)}` : "none"],
              ["Audio concealment", pctText(c.concealmentPctAvg)],
              ["Frame rate avg", c.fpsAvg == null ? "—" : `${Math.round(c.fpsAvg)} fps`],
              ["Resolution", resolution],
              ["Encoder limited", limited],
            ]}
          />
          <Group
            title="Who and where"
            rows={[
              ["User", <EndUser id={c.endUserId} name={c.endUserName} />],
              ["Browser", c.browser ?? "—"],
              ["OS", c.os ?? "—"],
              ["Device", c.device ?? "—"],
              ["Release", c.release ?? "—"],
              ["Environment", c.environment ?? "—"],
              [
                "Session",
                <Link to="/sessions/$id" params={{ id: c.sessionId }} search={{ at: startMs } as never} className="mono">
                  {c.sessionId}
                </Link>,
              ],
            ]}
          />
        </div>
      </section>

      <section className="card calls-card" aria-label="Quality over the call">
        <h2>Quality over the call</h2>
        {present.length === 0 ? (
          <p className="muted small call-none">{c.failedToConnect ? "This call never connected, so there are no sampled statistics." : "No sampled statistics for this call."}</p>
        ) : (
          <div className="call-multiples">
            {present.map((m) => (
              <div key={String(m.key)} className="call-multiple">
                <h3>
                  {m.label} <span className="muted small">{m.unit}</span>
                </h3>
                <LineChart
                  ariaLabel={`${m.label} over the call`}
                  height={130}
                  yMax={m.yMax}
                  formatValue={m.format}
                  series={[{ name: m.label, color: m.color, points: series.map((p) => ({ t: p.t, v: typeof p[m.key] === "number" ? (p[m.key] as number) : FILL_GAPS.has(m.key) ? 0 : null })) }]}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card calls-card" aria-label="Call events">
        <h2>Events</h2>
        {(c.markers?.length ?? 0) === 0 ? (
          <p className="muted small call-none">No lifecycle events recorded.</p>
        ) : (
          <>
            <p className="muted small call-lead">Pick an event to open the replay at that moment.</p>
            <ul className="call-markers" data-testid="call-markers">
              {c.markers.map((mk, i) => (
                <li key={`${mk.t}-${i}`}>
                  <button type="button" onClick={() => seekTo(mk.t)} data-testid={`marker-${mk.kind}`}>
                    <span className="mono">{dur(Math.max(0, mk.t - startMs))}</span>
                    <span className={`badge ${SEV_CLASS[mk.severity ?? "info"]}`}>{mk.severity ?? "info"}</span>
                    <span className="call-marker-label">{mk.label ?? mk.kind.replace(/_/g, " ")}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {!!c.issues?.length && (
        <section className="card calls-card" aria-label="Issues detected on this call">
          <h2>Issues</h2>
          <ul className="call-issues">
            {c.issues.map((is) => (
              <li key={is.id}>
                <SeverityBadge severity={is.severity} />
                <button type="button" className="call-issue-open" onClick={() => seekTo(is.at)} title="Open the replay where this started">
                  {kindLabel(is.kind)}
                </button>
                <Link to="/issues/$clusterId" params={{ clusterId: is.clusterId }} search={{ ...listSearch, fromCall: c.id } as never} className="btn ghost sm">
                  View issue
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
